'use client';

/**
 * Add Product Action Section
 *
 * @component_role Explorer section type `add_product_action`
 * @business_rule Capability-gated action that opens the Add Product form tab.
 *   Hidden when the caller lacks the `discovery.create` capability in the
 *   capability store. Single action, no coupled state — independent of the
 *   search composer.
 *
 *   Labels come from the total-matrix `explorer_labels.discovery` dict
 *   (server-driven, language-aware). No hardcoded strings.
 *
 * @layer presentation
 * @arch_component_type section
 * @arch_domain discovery
 */

import { Package } from 'lucide-react';
import { useTabStore } from '@/lib/stores/tab-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { cn } from '@/lib/utils/cn';
import type { ExplorerSectionConfig, LabelsDict } from '@/lib/api/types';

export function AddProductActionSection({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config,
  labels: L,
}: {
  config: ExplorerSectionConfig;
  labels: LabelsDict;
}) {
  const openTab = useTabStore((s) => s.openTab);
  const canAddProduct = useCapabilityStore(
    (s) => s.strategyName('discovery', 'create') !== null,
  );

  if (!canAddProduct) return null;
  if (!L.add_product_label) return null; // labels not loaded yet

  return (
    <div className="px-3 py-2 border-b border-border">
      <button
        type="button"
        onClick={() =>
          openTab({
            id: 'add-product-form',
            type: 'add_product',
            title: L.add_product_label,
            entityId: 'add-product',
          })
        }
        className={cn(
          'w-full py-2 px-3 rounded-md text-sm',
          'flex items-center justify-center gap-2',
          'border-2 border-dashed border-border',
          'text-muted-foreground hover:text-foreground hover:border-primary/50',
          'transition-colors',
        )}
      >
        <Package className="w-4 h-4" />
        {L.add_product_label}
      </button>
    </div>
  );
}
